import { MessagesSquare, Scissors, Cable, Repeat, TrendingUp, ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { useSimplify } from "./simplify-context";

const STEPS = [
  {
    id: "step-understand",
    icon: MessagesSquare,
    num: "01",
    title: "Understand",
    line: "We learn how your business works.",
    body: "One short conversation. Walk us through a job — from the first call to the final invoice.",
  },
  {
    id: "step-simplify",
    icon: Scissors,
    num: "02",
    title: "Simplify",
    line: "We remove the unnecessary steps.",
    body: "The retyping, the chasing, the second spreadsheet — mapped, then removed at the source.",
  },
  {
    id: "step-connect",
    icon: Cable,
    num: "03",
    title: "Connect",
    line: "We connect the software you already have.",
    body: "Keep your tools. We make them share one record, so nothing needs to be entered twice.",
  },
  {
    id: "step-automate",
    icon: Repeat,
    num: "04",
    title: "Automate",
    line: "Repetitive work starts doing itself.",
    body: "Follow-ups, handoffs, invoices — the routine moves on its own, with your approval where it matters.",
  },
  {
    id: "step-improve",
    icon: TrendingUp,
    num: "05",
    title: "Improve",
    line: "We keep making the business easier to run.",
    body: "We measure the capacity you got back, then find the next thing worth simplifying.",
  },
];

export default function JTBD() {
  const { setOpen } = useSimplify();

  return (
    <section id="work" data-testid="jtbd-section" className="py-20 lg:py-32 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="grid grid-cols-12 gap-8 lg:gap-12 mb-14 lg:mb-20">
          <Reveal className="col-span-12 lg:col-span-6">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-4">
              Business simplification
            </p>
            <h2 className="font-display font-semibold tracking-tight leading-[1.12] text-brand-navy text-2xl sm:text-3xl lg:text-4xl">
              Five steps. That's the whole thing.
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="col-span-12 lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="text-base text-slate-600 leading-relaxed">
              No software to learn, no big project to survive. We do the
              connecting; you keep running the business.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {STEPS.map((step, i) => (
            <Reveal key={step.id} delay={i * 0.07} className="h-full">
              <div
                data-testid={step.id}
                className="group h-full rounded-2xl bg-white border border-slate-200 p-6 sm:p-7 transition-all duration-300 hover:border-brand-teal hover:-translate-y-0.5 hover:shadow-[0_24px_48px_-20px_rgba(0,34,210,0.25)]"
              >
                <div className="flex items-start justify-between mb-7">
                  <span className="w-10 h-10 rounded-full bg-brand-tealsoft text-brand-teal flex items-center justify-center transition-colors duration-300 group-hover:bg-brand-teal group-hover:text-white">
                    <step.icon size={17} strokeWidth={2} />
                  </span>
                  <span className="font-mono text-xs text-slate-300 group-hover:text-brand-teal transition-colors duration-300">
                    {step.num}
                  </span>
                </div>
                <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand-teal font-semibold mb-2">
                  {step.title}
                </p>
                <h3 className="font-display font-medium text-lg text-brand-navy leading-snug mb-2.5">
                  {step.line}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {step.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <button
              data-testid="steps-cta-button"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-3 rounded-full bg-brand-navy text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-teal hover:-translate-y-0.5"
            >
              Show me where I can simplify
              <ArrowRight size={18} />
            </button>
            <p className="text-sm text-slate-500">
              Starts with a free 15-minute business review.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
